// client/src/pages/Dashboard/Dashboard.js
/**
 * "Smittläge"-vyn: fyrfältare med vanligaste och snabbast växande symptom
 * respektive symptomkombinationer. Karta + LocationDataPanel under.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import API_CONFIG from '../../config/apiConfig';
import PostalCodeMap from '../MapView/components/PostalCodeMapLeaflet';
import LocationDataPanel from './components/LocationDataPanel';
import postalCodeService from '../../services/postalCodeService';
import { ALL_SYMPTOMS } from '../../components/shared/constants/symptoms';
import './Dashboard.css';

// ===== CONSTANTS =====
const GEO_SCOPES = {
  COUNTRY: 'country',
  CITY: 'city',
  POSTAL: 'postal'
};

const SWEDISH_CITIES = [
  'Stockholm', 'Göteborg', 'Malmö', 'Uppsala', 'Linköping',
  'Örebro', 'Västerås', 'Helsingborg', 'Norrköping', 'Jönköping',
  'Umeå', 'Lund', 'Borås', 'Sundsvall', 'Gävle'
];

// ===== STOCKHOLM FEVER DEMO DATA =====
const DEMO_AREAS = [
  // Central Stockholm
  { postalCode: '11140', city: 'Stockholm', position: { lat: 59.335, lng: 18.065 }, label: 'Norrmalm' },
  { postalCode: '11350', city: 'Stockholm', position: { lat: 59.345, lng: 18.055 }, label: 'Vasastan' },
  { postalCode: '11440', city: 'Stockholm', position: { lat: 59.340, lng: 18.085 }, label: 'Östermalm' },
  { postalCode: '11220', city: 'Stockholm', position: { lat: 59.332, lng: 18.028 }, label: 'Kungsholmen' },
  { postalCode: '11630', city: 'Stockholm', position: { lat: 59.315, lng: 18.070 }, label: 'Södermalm' },
  { postalCode: '11730', city: 'Stockholm', position: { lat: 59.305, lng: 18.055 }, label: 'Södermalm S' },
  // Inner suburbs
  { postalCode: '16740', city: 'Bromma', position: { lat: 59.340, lng: 17.940 }, label: 'Bromma' },
  { postalCode: '16960', city: 'Solna', position: { lat: 59.360, lng: 18.000 }, label: 'Solna' },
  { postalCode: '17170', city: 'Solna', position: { lat: 59.370, lng: 18.010 }, label: 'Solna N' },
  { postalCode: '17240', city: 'Sundbyberg', position: { lat: 59.365, lng: 17.975 }, label: 'Sundbyberg' },
  { postalCode: '18130', city: 'Lidingö', position: { lat: 59.365, lng: 18.140 }, label: 'Lidingö' },
  { postalCode: '12060', city: 'Stockholm', position: { lat: 59.280, lng: 18.080 }, label: 'Enskede' },
  { postalCode: '12130', city: 'Stockholm', position: { lat: 59.293, lng: 18.075 }, label: 'Johanneshov' },
  { postalCode: '12640', city: 'Stockholm', position: { lat: 59.302, lng: 17.975 }, label: 'Hägersten' },
  // Outer suburbs
  { postalCode: '12340', city: 'Farsta', position: { lat: 59.257, lng: 18.095 }, label: 'Farsta' },
  { postalCode: '12720', city: 'Skärholmen', position: { lat: 59.277, lng: 17.920 }, label: 'Skärholmen' },
  { postalCode: '16420', city: 'Kista', position: { lat: 59.403, lng: 17.945 }, label: 'Kista' },
  { postalCode: '16270', city: 'Vällingby', position: { lat: 59.365, lng: 17.880 }, label: 'Vällingby' },
  { postalCode: '13140', city: 'Nacka', position: { lat: 59.308, lng: 18.165 }, label: 'Nacka' },
  { postalCode: '14137', city: 'Huddinge', position: { lat: 59.237, lng: 17.982 }, label: 'Huddinge' },
  { postalCode: '18230', city: 'Danderyd', position: { lat: 59.395, lng: 18.085 }, label: 'Danderyd' },
  { postalCode: '18330', city: 'Täby', position: { lat: 59.433, lng: 18.070 }, label: 'Täby' },
  { postalCode: '19150', city: 'Sollentuna', position: { lat: 59.428, lng: 17.950 }, label: 'Sollentuna' },
  { postalCode: '17540', city: 'Järfälla', position: { lat: 59.410, lng: 17.865 }, label: 'Järfälla' },
  { postalCode: '16530', city: 'Hässelby', position: { lat: 59.360, lng: 17.835 }, label: 'Hässelby' },
];

// Pre-compute spatial relationships for cloud-like flow animation
const DEMO_EPICENTER = DEMO_AREAS[0].position; // Norrmalm = outbreak origin
const DEMO_DISTANCES = DEMO_AREAS.map(area => {
  const dx = area.position.lat - DEMO_EPICENTER.lat;
  const dy = area.position.lng - DEMO_EPICENTER.lng;
  return Math.sqrt(dx * dx + dy * dy);
});
const DEMO_MAX_DIST = Math.max(...DEMO_DISTANCES);

// Neighbor pairs: areas within ~0.05 degrees (~5km) for flow particles
const DEMO_NEIGHBORS = [];
for (let i = 0; i < DEMO_AREAS.length; i++) {
  for (let j = i + 1; j < DEMO_AREAS.length; j++) {
    const dx = DEMO_AREAS[i].position.lat - DEMO_AREAS[j].position.lat;
    const dy = DEMO_AREAS[i].position.lng - DEMO_AREAS[j].position.lng;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 0.05) DEMO_NEIGHBORS.push({ a: i, b: j, dist });
  }
}

// Fever counts per area per day — dramatic wave spreading from center
const DEMO_DAYS = [
  //  Norr Vasa Öst  Kung Söd  SödS Bro  Sol  SolN Sund Lid  Ens  Joh  Häg  Far  Skär Kis  Väll Nac  Hud  Dan  Täb  Soll Jär  Häss
  [  190,  25,  10,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0],  // Dag 1: Utbrott i Norrmalm
  [  260, 140,  90,  70,  20,   0,   0,   5,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0],  // Dag 2: Sprider sig centralt
  [  290, 220, 180, 160, 100,  50,  30,  35,  20,  15,  10,   8,   5,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0,   0],  // Dag 3: Når Södermalm + inre förorter
  [  240, 270, 260, 230, 200, 140, 100,  95,  80,  75,  55,  50,  45,  40,  18,  12,  10,   8,   8,   0,   0,   0,   0,   0,   0],  // Dag 4: Full innerstaden
  [  160, 210, 220, 200, 240, 210, 180, 170, 160, 150, 120, 130, 120, 110,  90,  80,  70,  60,  55,  30,  25,  15,  10,   8,   8],  // Dag 5: Spridd — förorter exploderar
  [   85, 115, 125, 105, 180, 190, 210, 220, 210, 200, 180, 190, 180, 170, 160, 150, 140, 130, 120, 100,  90,  70,  55,  45,  40],  // Dag 6: Centrum avtar, yttre peakar
  [   35,  55,  60,  45,  85, 110, 170, 190, 200, 195, 180, 210, 200, 180, 200, 190, 180, 180, 170, 160, 150, 130, 120, 110, 100],  // Dag 7: Vågen har passerat centrum
];

const DEMO_DAY_LABELS = [
  'Dag 1 — Utbrott upptäcks i Norrmalm',
  'Dag 2 — Sprider sig till innerstaden',
  'Dag 3 — Når Södermalm och inre förorter',
  'Dag 4 — Hela innerstaden drabbad',
  'Dag 5 — Förorterna exploderar',
  'Dag 6 — Centrum avtar, yttre peakar',
  'Dag 7 — Vågen har passerat centrum',
];

// ===== MAIN COMPONENT =====
const Dashboard = () => {
  const { t } = useTranslation();

  // ✅ Symptom translation helper
  const tSymptom = (name) => t(`symptoms.${name}`, name);

  // ✅ Combination translation helper (translates each symptom in "Feber+Hosta")
  const tCombination = (comboString) => {
    if (!comboString) return '';
    return comboString.split('+').map(s => tSymptom(s.trim())).join('+');
  };

  // ----- GEO SCOPE STATE (Master) -----
  const [geoScope, setGeoScope] = useState(GEO_SCOPES.COUNTRY);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedPostalCode, setSelectedPostalCode] = useState('');
  const [confirmedPostalCode, setConfirmedPostalCode] = useState('');
  
  // ----- WEEK STATE -----
  const [selectedWeek, setSelectedWeek] = useState(47);
  
  // ----- SYMPTOM FILTER STATE -----
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  
  // ----- DATA STATE - SYMPTOM -----
  const [popularSymptoms, setPopularSymptoms] = useState([]);
  const [growingSymptoms, setGrowingSymptoms] = useState([]);
  
  // ----- DATA STATE - COMBINATIONS (NEW!) -----
  const [popularCombinations, setPopularCombinations] = useState([]);
  const [growingCombinations, setGrowingCombinations] = useState([]);
  
  // ----- MAP & GENERAL DATA -----
  const [mapData, setMapData] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  
  // ----- LOADING STATE -----
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ----- DEMO STATE (smooth animation) -----
  const [demoActive, setDemoActive] = useState(false);
  const [demoProgress, setDemoProgress] = useState(0); // 0.0 → 6.0 continuous
  const [demoPlaying, setDemoPlaying] = useState(false);
  const [demoBreath, setDemoBreath] = useState(0); // Always ticks when active → cloud alive
  const demoRafRef = useRef(null);
  const demoLastTimeRef = useRef(null);
  const breathRafRef = useRef(null);
  const breathLastRef = useRef(null);

  // Derived integer day for labels
  const demoDay = Math.min(Math.floor(demoProgress), 6);

  // Lerp helper
  const lerp = (a, b, t) => a + (b - a) * t;

  // Interpolated demo data — cloud-flow animation with continuous breathing
  const demoMapData = useMemo(() => {
    if (!demoActive) return null;
    
    const dayA = Math.min(Math.floor(demoProgress), 6);
    const dayB = Math.min(dayA + 1, 6);
    const t = demoProgress - dayA; // 0.0 → 1.0 fraction between days
    
    const countsA = DEMO_DAYS[dayA];
    const countsB = DEMO_DAYS[dayB];
    const result = [];
    
    // ===== EFFECT 1: Wave-front + micro-drift + scatter sub-points =====
    const WAVE_SPREAD = 0.25;
    const DRIFT_AMP = 0.002;    // ~200m sinusoidal drift
    const SCATTER_RAD = 0.008;  // ~800m scatter radius for sub-points
    const SUB_POINTS = 3;       // extra points per area for organic cloud shape
    
    DEMO_AREAS.forEach((area, i) => {
      // Wave-front: outer areas lag behind center
      const distRatio = DEMO_DISTANCES[i] / DEMO_MAX_DIST;
      const localT = Math.max(0, Math.min(1, 
        t - distRatio * WAVE_SPREAD + WAVE_SPREAD * 0.4
      ));
      
      const count = Math.round(lerp(countsA[i], countsB[i], localT));
      if (count <= 0) return;
      
      // Breath-driven drift (uses demoBreath → never stops)
      const driftLat = Math.sin(demoBreath * 0.7 + i * 1.7) * DRIFT_AMP;
      const driftLng = Math.cos(demoBreath * 0.5 + i * 2.3) * DRIFT_AMP;
      
      // Main point (full count for data accuracy)
      result.push({
        ...area,
        position: {
          lat: area.position.lat + driftLat,
          lng: area.position.lng + driftLng,
        },
        count,
        symptomCount: count,
        totalCount: Math.round(count * 1.6),
      });
      
      // Scatter sub-points (visual haze, ~15% intensity) → organic cloud shape
      const subCount = Math.max(2, Math.round(count * 0.15));
      for (let s = 0; s < SUB_POINTS; s++) {
          const angle = (s / SUB_POINTS) * Math.PI * 2 + demoBreath * 0.3 + i;
          const radius = SCATTER_RAD * (0.6 + 0.4 * Math.sin(demoBreath * 0.9 + s * 2.5 + i));
          
          result.push({
            postalCode: `sc_${i}_${s}`,
            city: area.city,
            position: {
              lat: area.position.lat + Math.cos(angle) * radius + driftLat * 0.5,
              lng: area.position.lng + Math.sin(angle) * radius + driftLng * 0.5,
            },
            count: subCount,
            symptomCount: subCount,
            totalCount: subCount,
          });
        }
    });
    
    // ===== EFFECT 2: Flow particles between neighbors =====
    if (t > 0.03 && t < 0.97) {
      DEMO_NEIGHBORS.forEach(({ a, b }) => {
        const deltaA = countsB[a] - countsA[a];
        const deltaB = countsB[b] - countsA[b];
        const flow = deltaB - deltaA;
        
        if (Math.abs(flow) < 25) return;
        
        const fromIdx = flow > 0 ? a : b;
        const toIdx = flow > 0 ? b : a;
        const fromPos = DEMO_AREAS[fromIdx].position;
        const toPos = DEMO_AREAS[toIdx].position;
        const magnitude = Math.abs(flow);
        
        const SIGMA = 0.18;
        for (let p = 0; p < 5; p++) {
          const pathPos = 0.1 + p * 0.2;
          const gaussian = Math.exp(-Math.pow(t - pathPos, 2) / (2 * SIGMA * SIGMA));
          const flowCount = Math.round(magnitude * 0.2 * gaussian);
          
          if (flowCount > 2) {
            const wobble = Math.sin(demoBreath * 3 + p * 2.1 + fromIdx) * 0.003;
            result.push({
              postalCode: `fl_${fromIdx}_${toIdx}_${p}`,
              city: 'Stockholm',
              position: {
                lat: lerp(fromPos.lat, toPos.lat, pathPos) + wobble,
                lng: lerp(fromPos.lng, toPos.lng, pathPos) - wobble * 0.7,
              },
              count: flowCount,
              symptomCount: flowCount,
              totalCount: flowCount,
            });
          }
        }
      });
    }
    
    // ===== EFFECT 3: Ambient haze — drifting ghost points when static =====
    // Always active → the map never looks frozen
    if (t < 0.03 || t > 0.97) {
      const currentCounts = t < 0.5 ? countsA : countsB;
      // Pick top 8 areas by count → emit slow-drifting haze
      const ranked = currentCounts
        .map((c, i) => ({ i, c }))
        .sort((a, b) => b.c - a.c)
        .slice(0, 8);
      
      ranked.forEach(({ i, c }) => {
        if (c < 20) return;
        const pos = DEMO_AREAS[i].position;
        for (let h = 0; h < 3; h++) {
          const angle = demoBreath * 0.2 + h * 2.1 + i * 0.7;
          const dist = 0.012 + 0.006 * Math.sin(demoBreath * 0.4 + h + i);
          const hazeCount = Math.round(c * 0.08);
          if (hazeCount > 1) {
            result.push({
              postalCode: `hz_${i}_${h}`,
              city: 'Stockholm',
              position: {
                lat: pos.lat + Math.cos(angle) * dist,
                lng: pos.lng + Math.sin(angle) * dist,
              },
              count: hazeCount,
              symptomCount: hazeCount,
              totalCount: hazeCount,
            });
          }
        }
      });
    }
    
    return result;
  }, [demoActive, demoProgress, demoBreath]);

  // requestAnimationFrame animation loop
  const SECONDS_PER_DAY = 3.0; // Slower for cloud-flow visibility
  
  useEffect(() => {
    if (!demoPlaying) {
      demoLastTimeRef.current = null;
      return;
    }
    
    const animate = (timestamp) => {
      if (!demoLastTimeRef.current) {
        demoLastTimeRef.current = timestamp;
      }
      
      const delta = (timestamp - demoLastTimeRef.current) / 1000; // seconds
      demoLastTimeRef.current = timestamp;
      
      setDemoProgress(prev => {
        const next = prev + delta / SECONDS_PER_DAY;
        if (next >= 6.0) {
          setDemoPlaying(false);
          return 6.0;
        }
        return next;
      });
      
      demoRafRef.current = requestAnimationFrame(animate);
    };
    
    demoRafRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (demoRafRef.current) cancelAnimationFrame(demoRafRef.current);
    };
  }, [demoPlaying]);

  // Continuous breathing loop — runs whenever demo is active, even paused
  useEffect(() => {
    if (!demoActive) {
      breathLastRef.current = null;
      return;
    }
    
    const breathe = (timestamp) => {
      if (!breathLastRef.current) breathLastRef.current = timestamp;
      const delta = (timestamp - breathLastRef.current) / 1000;
      breathLastRef.current = timestamp;
      setDemoBreath(prev => prev + delta);
      breathRafRef.current = requestAnimationFrame(breathe);
    };
    
    breathRafRef.current = requestAnimationFrame(breathe);
    return () => {
      if (breathRafRef.current) cancelAnimationFrame(breathRafRef.current);
    };
  }, [demoActive]);

  const startDemo = useCallback(() => {
    setDemoActive(true);
    setDemoProgress(0);
    setDemoBreath(0);
    setDemoPlaying(true);
  }, []);

  const stopDemo = useCallback(() => {
    setDemoActive(false);
    setDemoPlaying(false);
    setDemoProgress(0);
    setDemoBreath(0);
    if (demoRafRef.current) cancelAnimationFrame(demoRafRef.current);
    if (breathRafRef.current) cancelAnimationFrame(breathRafRef.current);
  }, []);

  // ===== GEO LABEL =====
  const geoLabel = useMemo(() => {
    if (geoScope === GEO_SCOPES.POSTAL && confirmedPostalCode) {
      return confirmedPostalCode;
    }
    if (geoScope === GEO_SCOPES.CITY && selectedCity) {
      return selectedCity;
    }
    return t('common.sweden');
  }, [geoScope, selectedCity, confirmedPostalCode, t]);

  // ===== MAP CENTER & ZOOM =====
  const mapConfig = useMemo(() => {
    if (geoScope === GEO_SCOPES.POSTAL && confirmedPostalCode) {
      return { center: [59.33, 18.07], zoom: 13 };
    }
    if (geoScope === GEO_SCOPES.CITY && selectedCity) {
      const cityCenters = {
        'Stockholm': [59.33, 18.07],
        'Göteborg': [57.71, 11.97],
        'Malmö': [55.60, 13.00],
        'Uppsala': [59.86, 17.64],
        'Linköping': [58.41, 15.62],
        'Örebro': [59.27, 15.21],
        'Västerås': [59.61, 16.54],
        'Helsingborg': [56.05, 12.69],
        'Norrköping': [58.59, 16.18],
        'Jönköping': [57.78, 14.16],
        'Umeå': [63.83, 20.26],
        'Lund': [55.70, 13.19],
        'Borås': [57.72, 12.94],
        'Sundsvall': [62.39, 17.31],
        'Gävle': [60.67, 17.14]
      };
      return { 
        center: cityCenters[selectedCity] || [62.2, 16.5], 
        zoom: 11 
      };
    }
    return { center: [62.2, 16.5], zoom: 5 };
  }, [geoScope, selectedCity, confirmedPostalCode]);

  // ===== FETCH ALL ANALYSIS DATA (FYRFÄLTARE) =====
  const fetchAnalysisData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build geo param (city OR postalCode)
      let geoParam = '';
      if (geoScope === GEO_SCOPES.CITY && selectedCity) {
        geoParam = `&city=${encodeURIComponent(selectedCity)}`;
      } else if (geoScope === GEO_SCOPES.POSTAL && confirmedPostalCode) {
        geoParam = `&postalCode=${confirmedPostalCode}`;
      }
      
      // ✅ FIXED: Dynamic weeks based on selectedWeek
      const growingWeeks = `${Math.max(1, selectedWeek-2)},${Math.max(1, selectedWeek-1)},${selectedWeek}`;
      
      // ===== PARALLEL FETCH: 4 API calls =====
      const [
        popularSymptomsRes,
        growingSymptomsRes,
        popularCombosRes,
        growingCombosRes
      ] = await Promise.all([
        // 1. Popular symptoms
        fetch(`${API_CONFIG.baseUrl}/health-data/symptom-analysis?type=popular&week=${selectedWeek}${geoParam}`),
        // 2. Growing symptoms - ✅ FIXED: Dynamic weeks
        fetch(`${API_CONFIG.baseUrl}/health-data/symptom-analysis?type=growing&weeks=${growingWeeks}${geoParam}`),
        // 3. Popular combinations
        fetch(`${API_CONFIG.baseUrl}/health-data/combination-analysis?type=popular&week=${selectedWeek}${geoParam}`),
        // 4. Growing combinations - ✅ FIXED: Dynamic weeks
        fetch(`${API_CONFIG.baseUrl}/health-data/combination-analysis?type=growing&weeks=${growingWeeks}${geoParam}`)
      ]);
      
      // Parse responses
      const [popularSymptomsData, growingSymptomsData, popularCombosData, growingCombosData] = await Promise.all([
        popularSymptomsRes.json(),
        growingSymptomsRes.json(),
        popularCombosRes.json(),
        growingCombosRes.json()
      ]);
      
      // Update state - Symptoms
      if (popularSymptomsData.success && popularSymptomsData.data) {
        setPopularSymptoms(popularSymptomsData.data.symptoms || []);
        setTotalReports(popularSymptomsData.data.totalReports || 0);
      }
      
      if (growingSymptomsData.success && growingSymptomsData.data) {
        setGrowingSymptoms(growingSymptomsData.data.symptoms || []);
      }
      
      // Update state - Combinations
      if (popularCombosData.success && popularCombosData.data) {
        setPopularCombinations(popularCombosData.data.combinations || []);
      }
      
      if (growingCombosData.success && growingCombosData.data) {
        setGrowingCombinations(growingCombosData.data.combinations || []);
      }
      
      console.log(`✅ FYRFÄLTARE loaded for ${geoLabel}:`, {
        popularSymptoms: popularSymptomsData.data?.symptoms?.length || 0,
        growingSymptoms: growingSymptomsData.data?.symptoms?.length || 0,
        popularCombinations: popularCombosData.data?.combinations?.length || 0,
        growingCombinations: growingCombosData.data?.combinations?.length || 0
      });
      
    } catch (err) {
      console.error('❌ Error fetching analysis data:', err);
      setError(t('dashboard.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [geoScope, selectedCity, confirmedPostalCode, selectedWeek, geoLabel, t]);

  // ===== FETCH MAP DATA =====
  const fetchMapData = useCallback(async () => {
    try {
      let url = `${API_CONFIG.baseUrl}/health-data/postal-groups?week=${selectedWeek}`;
      console.log('🗺️ Dashboard fetchMapData:', { selectedWeek, selectedSymptoms });
      
      if (geoScope === GEO_SCOPES.CITY && selectedCity) {
        url += `&city=${encodeURIComponent(selectedCity)}`;
      } else if (geoScope === GEO_SCOPES.POSTAL && confirmedPostalCode) {
        url += `&postalCode=${confirmedPostalCode}`;
      }
      
      if (selectedSymptoms.length > 0) {
        url += `&symptoms=${encodeURIComponent(selectedSymptoms.join(','))}`;
        url += `&matchMode=combination`;
      }
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success && result.data) {
  const dataWithRisk = result.data.map(item => ({
    ...item,
    count: item.symptomCount || item.count || 0,
    risk: calculateRisk(item.symptomCount || item.count || 0)
  }));
  
  // DEBUG: Se vad som sparas
  const totalCount = dataWithRisk.reduce((sum, d) => sum + (d.count || 0), 0);
  console.log('📊 setMapData:', { 
    items: dataWithRisk.length, 
    totalCount,
    firstItem: dataWithRisk[0] 
  });
  
  setMapData(dataWithRisk);
}
      
    } catch (err) {
      console.error('❌ Error fetching map data:', err);
    }
  }, [geoScope, selectedCity, confirmedPostalCode, selectedWeek, selectedSymptoms]);

  // ===== RISK CALCULATION =====
  const calculateRisk = (count) => {
    if (count >= 50) return { level: 'high', color: '#ff453a' };
    if (count >= 20) return { level: 'medium', color: '#ff9f0a' };
    return { level: 'low', color: '#32d74b' };
  };

  // ===== EFFECTS =====
  useEffect(() => {
    fetchAnalysisData();
  }, [fetchAnalysisData]);

  useEffect(() => {
    fetchMapData();
  }, [fetchMapData]);

  // ===== HANDLERS =====
  const handleGeoScopeChange = (scope) => {
    setGeoScope(scope);
    if (scope === GEO_SCOPES.COUNTRY) {
      setSelectedCity('');
      setSelectedPostalCode('');
      setConfirmedPostalCode('');
    } else if (scope === GEO_SCOPES.CITY) {
      setSelectedPostalCode('');
      setConfirmedPostalCode('');
    }
  };

  const handleCityChange = (city) => {
    setSelectedCity(city);
    setGeoScope(GEO_SCOPES.CITY);
    setSelectedPostalCode('');
    setConfirmedPostalCode('');
  };

  const handlePostalCodeChange = (e) => {
    const value = e.target.value.replace(/\s/g, '');
    setSelectedPostalCode(value);
  };

  const handlePostalCodeSearch = async () => {
    if (selectedPostalCode.length >= 5) {
      setConfirmedPostalCode(selectedPostalCode);
      setGeoScope(GEO_SCOPES.POSTAL);
      
      // ✅ NEW: Auto-fill city from postal code
      const city = await postalCodeService.lookupCityByPostalCode(selectedPostalCode);
      if (city) {
        setSelectedCity(city);
        console.log(`📮 Auto-filled city: ${selectedPostalCode} → ${city}`);
      } else {
        setSelectedCity('');
      }
    }
  };

  // Tillåt Enter-tangent för att söka
  const handlePostalCodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      handlePostalCodeSearch();
    }
  };

  const handleSymptomToggle = (symptom) => {
    setSelectedSymptoms(prev => {
      if (prev.includes(symptom)) {
        return prev.filter(s => s !== symptom);
      }
      return [...prev, symptom];
    });
  };

  const handleSymptomClick = (symptomName) => {
    setSelectedSymptoms([symptomName]);
  };

  // Handle combination click - filter on all symptoms in combination
  const handleCombinationClick = (symptoms) => {
    setSelectedSymptoms(symptoms);
  };

  const handleMapClick = useCallback((postalCode) => {
    setSelectedPostalCode(postalCode);
    setConfirmedPostalCode(postalCode);
    setGeoScope(GEO_SCOPES.POSTAL);
    setSelectedCity('');
  }, []);

  // ===== RENDER =====
  return (
    <div className="dashboard-container">
      
      {/* HEADER */}
      <header className="dashboard-header">
        <div className="header-content">
          <h1>{t('dashboard.title', { location: geoLabel })}</h1>
          <div className="header-meta">
            <span className="week-badge">{t('dashboard.weekLabel')}</span>
            <select 
              value={selectedWeek} 
              onChange={(e) => setSelectedWeek(Number(e.target.value))}
              className="week-selector"
            >
              {[40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53].map(w => (
                <option key={w} value={w}>{t('dashboard.weekOption', { week: w })}</option>
              ))}
            </select>
          </div>
        </div>
        {error && <div className="error-message">{error}</div>}
      </header>

      {/* GEO SCOPE SELECTOR */}
      <section className="geo-scope-section">
        <h3 className="section-label">{t('dashboard.selectPerspective')}</h3>
        <div className="geo-scope-controls">
          <button
            className={`geo-btn ${geoScope === GEO_SCOPES.COUNTRY ? 'active' : ''}`}
            onClick={() => handleGeoScopeChange(GEO_SCOPES.COUNTRY)}
          >
            {t('dashboard.sweden')}
          </button>
          
          <div className="geo-dropdown">
            <select
              value={selectedCity}
              onChange={(e) => handleCityChange(e.target.value)}
              className={`city-select ${geoScope === GEO_SCOPES.CITY ? 'active' : ''}`}
            >
              <option value="">{t('dashboard.selectCity')}</option>
              {SWEDISH_CITIES.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
          
          <div className="postal-input-wrapper">
            <input
              type="text"
              value={selectedPostalCode}
              onChange={handlePostalCodeChange}
              onKeyDown={handlePostalCodeKeyDown}
              placeholder={t('dashboard.postalCodePlaceholder')}
              maxLength="5"
              className={`postal-input ${geoScope === GEO_SCOPES.POSTAL ? 'active' : ''}`}
            />
            <button
              className="postal-search-btn"
              onClick={handlePostalCodeSearch}
              disabled={selectedPostalCode.length < 5}
            >
              {t('dashboard.search')}
            </button>
          </div>
        </div>
      </section>

      {/* ===== FYRFÄLTARE - STATS CARDS ===== */}
      <section className="stats-section">
        <div className="stats-grid four-panel">
          
          {/* 1. VANLIGASTE SYMPTOM */}
          <div className="stats-card popular-symptoms">
            <div className="card-header">
              <h3>{t('dashboard.popularSymptoms')}</h3>
              <span className="card-subtitle">{t('dashboard.weekAndLocation', { week: selectedWeek, location: geoLabel })}</span>
            </div>
            <div className="card-content">
              {loading ? (
                <div className="loading-placeholder">{t('dashboard.loading')}</div>
              ) : popularSymptoms.length > 0 ? (
                <ul className="stats-symptom-list">
                  {popularSymptoms.slice(0, 5).map((symptom, index) => (
                    <li 
                      key={symptom.name} 
                      className="stats-symptom-item clickable"
                      onClick={() => handleSymptomClick(symptom.name)}
                    >
                      <span className="rank">#{index + 1}</span>
                      <span className="name">{tSymptom(symptom.name)}</span>
                      <span className="count">{symptom.count}p</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-data">{t('dashboard.noData')}</div>
              )}
            </div>
            <div className="card-footer">
              {t('dashboard.totalReports', { count: totalReports })}
            </div>
          </div>

          {/* 2. SNABBAST VÄXANDE SYMPTOM */}
          <div className="stats-card growing-symptoms">
            <div className="card-header">
              <h3>{t('dashboard.growingSymptoms')}</h3>
              <span className="card-subtitle">{t('dashboard.trendAndLocation', { from: Math.max(1, selectedWeek-2), to: selectedWeek, location: geoLabel })}</span>
            </div>
            <div className="card-content">
              {loading ? (
                <div className="loading-placeholder">{t('dashboard.loading')}</div>
              ) : growingSymptoms.length > 0 ? (
                <ul className="stats-symptom-list">
                  {growingSymptoms.slice(0, 5).map((symptom, index) => (
                    <li 
                      key={symptom.name} 
                      className="stats-symptom-item clickable"
                      onClick={() => handleSymptomClick(symptom.name)}
                    >
                      <span className="rank">#{index + 1}</span>
                      <span className="name">{tSymptom(symptom.name)}</span>
                      <span className="growth">+{symptom.growth}%</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-data">{t('dashboard.noData')}</div>
              )}
            </div>
            <div className="card-footer">
              {t('dashboard.lastThreeWeeks')}
            </div>
          </div>

          {/* 3. VANLIGASTE KOMBINATIONER */}
          <div className="stats-card popular-combos">
            <div className="card-header">
              <h3>{t('dashboard.popularCombinations')}</h3>
              <span className="card-subtitle">{t('dashboard.weekAndLocation', { week: selectedWeek, location: geoLabel })}</span>
            </div>
            <div className="card-content">
              {loading ? (
                <div className="loading-placeholder">{t('dashboard.loading')}</div>
              ) : popularCombinations.length > 0 ? (
                <ul className="combination-list">
                  {popularCombinations.slice(0, 5).map((combo, index) => (
                    <li 
                      key={combo.combination} 
                      className="combination-item clickable"
                      onClick={() => handleCombinationClick(combo.symptoms)}
                    >
                      <span className="rank">#{index + 1}</span>
                      <span className="combo-name" title={tCombination(combo.combination)}>{tCombination(combo.combination)}</span>
                      <span className="count">{combo.count}p</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-data">{t('dashboard.noData')}</div>
              )}
            </div>
            <div className="card-footer">
              {t('dashboard.combinationsWithMultiple')}
            </div>
          </div>

          {/* 4. SNABBAST VÄXANDE KOMBINATIONER */}
          <div className="stats-card growing-combos">
            <div className="card-header">
              <h3>{t('dashboard.growingCombinations')}</h3>
              <span className="card-subtitle">{t('dashboard.trendAndLocation', { from: Math.max(1, selectedWeek-2), to: selectedWeek, location: geoLabel })}</span>
            </div>
            <div className="card-content">
              {loading ? (
                <div className="loading-placeholder">{t('dashboard.loading')}</div>
              ) : growingCombinations.length > 0 ? (
                <ul className="combination-list">
                  {growingCombinations.slice(0, 5).map((combo, index) => (
                    <li 
                      key={combo.combination} 
                      className="combination-item clickable"
                      onClick={() => handleCombinationClick(combo.symptoms)}
                    >
                      <span className="rank">#{index + 1}</span>
                      <span className="combo-name" title={tCombination(combo.combination)}>{tCombination(combo.combination)}</span>
                      <span className="growth">+{combo.growth}%</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-data">{t('dashboard.noData')}</div>
              )}
            </div>
            <div className="card-footer">
              {t('dashboard.lastThreeWeeks')}
            </div>
          </div>
          
        </div>
      </section>

      {/* SYMPTOM FILTER */}
      <section className="filter-section">
        <div className="filter-header">
          <h3 className="section-label">{t('dashboard.symptomFilter')}</h3>
        </div>
        
        <div className="symptom-buttons">
          {ALL_SYMPTOMS.map(symptom => (
            <button
              key={symptom}
              className={`symptom-btn ${selectedSymptoms.includes(symptom) ? 'selected' : ''}`}
              onClick={() => handleSymptomToggle(symptom)}
            >
              {tSymptom(symptom)}
            </button>
          ))}
        </div>
        
        {selectedSymptoms.length > 0 && (
          <div className="filter-summary">
            <span>{t('dashboard.showing', { symptoms: selectedSymptoms.map(s => tSymptom(s)).join(' + ') })}</span>
            <button 
              className="clear-filter-btn"
              onClick={() => setSelectedSymptoms([])}
            >
              {t('dashboard.clearFilter')}
            </button>
          </div>
        )}
      </section>

      {/* LOCATION DATA PANEL - Ovanför kartan */}
      <LocationDataPanel
        geoScope={geoScope}
        selectedCity={selectedCity}
        confirmedPostalCode={confirmedPostalCode}
        selectedSymptoms={selectedSymptoms}
        selectedWeek={selectedWeek}
      />

      {/* MAP SECTION */}
      <section className="map-section">
        <h3 className="section-label">
          {demoActive ? (
            <>🔴 Feber-demo — Stockholm<span className="map-info">{DEMO_DAY_LABELS[demoDay]}</span></>
          ) : (
            <>{t('dashboard.mapTitle')}<span className="map-info">{t('dashboard.mapInfo', { areas: mapData.length, total: mapData.reduce((s,d) => s + (d.count||0), 0) })}</span></>
          )}
        </h3>
        <PostalCodeMap
          data={demoActive ? demoMapData : mapData}
          selectedPostalCode={demoActive ? '' : selectedPostalCode}
          onPostalCodeClick={demoActive ? () => {} : handleMapClick}
          mapCenter={demoActive ? [59.33, 18.05] : mapConfig.center}
          zoom={demoActive ? 11 : mapConfig.zoom}
          visualizationType="heatmap"
          circleOpacity={0.6}
          loading={loading && !demoActive}
          heatmapConfig={demoActive ? {
            radius: 34,       // Extra large → blobs merge into continuous cloud
            blur: 26,         // Very soft edges
            minOpacity: 0.55, // Strong presence
          } : null}
        />

        {/* DEMO OVERLAY — dag-indikator + smooth progress */}
        {demoActive && (
          <div className="demo-overlay">
            <div className="demo-day-badge">
              <span className="demo-day-number">Dag {demoDay + 1}</span>
              <span className="demo-day-cases">
                {demoMapData.filter(d => /^\d/.test(d.postalCode)).reduce((s, d) => s + d.count, 0)} feberfall
              </span>
            </div>
            <div className="demo-progress-bar-wrap">
              <div 
                className="demo-progress-bar-fill"
                style={{ width: `${(demoProgress / 6) * 100}%` }}
              />
              {DEMO_DAYS.map((_, i) => (
                <div 
                  key={i}
                  className={`demo-progress-dot ${i === demoDay ? 'active' : ''} ${i < demoDay ? 'past' : ''}`}
                  style={{ left: `${(i / 6) * 100}%` }}
                />
              ))}
            </div>
          </div>
        )}

        {/* CONTROLS — stepper ELLER demo-kontroller */}
        <div className="map-week-stepper">
          {demoActive ? (
            <>
              <button
                className="week-step-btn"
                onClick={() => { setDemoPlaying(false); setDemoProgress(p => Math.max(0, Math.floor(p) - 1)); }}
                disabled={demoDay <= 0}
              >◀</button>
              <button
                className={`demo-play-btn ${demoPlaying ? 'playing' : ''}`}
                onClick={() => {
                  if (demoPlaying) {
                    setDemoPlaying(false);
                  } else {
                    if (demoProgress >= 6.0) setDemoProgress(0);
                    setDemoPlaying(true);
                  }
                }}
              >
                {demoPlaying ? '⏸' : '▶'}
              </button>
              <button
                className="week-step-btn"
                onClick={() => { setDemoPlaying(false); setDemoProgress(p => Math.min(6, Math.floor(p) + 1)); }}
                disabled={demoDay >= 6}
              >▶</button>
              <button className="demo-stop-btn" onClick={stopDemo}>✕ Stäng</button>
            </>
          ) : (
            <>
              <button
                className="week-step-btn"
                onClick={() => setSelectedWeek(w => Math.max(40, w - 1))}
                disabled={selectedWeek <= 40}
              >◀</button>
              <span className="week-step-label">
                {t('dashboard.weekOption', { week: selectedWeek })}
              </span>
              <button
                className="week-step-btn"
                onClick={() => setSelectedWeek(w => Math.min(53, w + 1))}
                disabled={selectedWeek >= 53}
              >▶</button>
              <button className="demo-start-btn" onClick={startDemo}>
                🎬 Demo
              </button>
            </>
          )}
        </div>
      </section>

      {/* LOADING OVERLAY */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">{t('dashboard.loadingData')}</div>
        </div>
      )}
      
    </div>
  );
};

export default Dashboard;
